import React from 'react';

import type { FrontendPermissionId } from '@personnel/domain/types/PermissionsConst';

/**
 * Permission Debug Info Component
 *
 * Displays debug information for a permission including frontend ID and backend IDs.
 * Only rendered when debug mode is active (development only).
 *
 * @param {PermissionDebugInfoProps} props - Component properties
 * @returns {React.ReactElement} Debug info component
 *
 * @example
 * <PermissionDebugInfo
 *   frontendId="org-menu"
 *   backendIds={['admin.store']}
 *   componentType="checkbox"
 * />
 */

interface PermissionDebugInfoProps {
  /** Frontend permission identifier */
  frontendId: FrontendPermissionId;
  /** Backend permission identifiers that will be sent to the server */
  backendIds: readonly string[];
  /** Type of component (for display purposes) */
  componentType: 'switch' | 'checkbox';
}

const styles = {
  container: 'mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 font-mono text-xs',
  header: 'flex items-center gap-1 text-amber-700 font-semibold mb-1',
  icon: '🐛',
  row: 'text-amber-600',
  label: 'text-amber-500',
  value: 'text-amber-800 select-all',
  emptyBackendIds: 'text-red-500 font-semibold',
  backendIdsList: 'flex flex-wrap gap-1 mt-0.5',
  backendIdTag: 'bg-amber-100 px-1.5 py-0.5 rounded text-amber-800 select-all',
};

export const PermissionDebugInfo = ({
  frontendId,
  backendIds,
  componentType,
}: PermissionDebugInfoProps): React.ReactElement => {
  const hasBackendIds = backendIds.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span>{styles.icon}</span>
        <span>DEBUG [{componentType.toUpperCase()}]</span>
      </div>

      <div className={styles.row}>
        <span className={styles.label}>frontendId: </span>
        <span className={styles.value}>{frontendId}</span>
      </div>

      <div className={styles.row}>
        <span className={styles.label}>backendIds: </span>
        {hasBackendIds ? (
          <div className={styles.backendIdsList}>
            {backendIds.map((id) => (
              <span className={styles.backendIdTag} key={id}>
                {id}
              </span>
            ))}
          </div>
        ) : (
          <span className={styles.emptyBackendIds}>⚠️ EMPTY (TBD)</span>
        )}
      </div>
    </div>
  );
};
